import { useEffect, useState, useRef } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  Grid,
  IconButton,
  LinearProgress,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Search, Refresh, Clear, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import { GridColDef } from '@mui/x-data-grid';
import { DataGrid } from '@mui/x-data-grid';
import { apiClient } from "../helper/api";
import useAuth, { authData } from "../context/AuthProvider";
import formatPrice from "../helper/currencyFormatter";
import Swal from "sweetalert2";
import { formatUtcToLocal, safeMoment, writeXlsxFile } from "../helper/utils";
import { StateReportModel } from "../models/StateReportModel";
import moment from "moment";
import { StateReportPreviewDialog } from "../components/Elements/StateReportPreviewDialog";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import { RiExpandUpDownLine } from "react-icons/ri";
 
interface Partner {
  partnersId: number;
  customerName: string;
}

export const ClientStateReports: React.FC<{ currentUser: authData | undefined }> = ({ currentUser }) => {
  const [loading, setloading] = useState(false);
  const { user } = useAuth();
  const [itemList, setItemList] = useState<StateReportModel[]>([]);
  const [pageSize, setPageSize] = useState(50);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sortModel, setSortModel] = useState([{ field: "", sort: "" }]);
  const [filterModel, setFilterModel] = useState<{ items: { columnField: string, value: string, operatorValue: string }[] }>({ items: [] });
  const [selectedHomeState, setSelectedHomeState] = useState('');
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [selectedPartner, setSelectedPartner] = useState('');
  const [partners, setPartners] = useState<Partner[]>([]);
  const [selectedTab, setSelectedTab] = useState<number>(1);
  const selectedPartnerRef = useRef<string>('0');
  const selectedStateRef = useRef<number>(0);
  const startDateRef = useRef<string>('');
  const endDateRef = useRef<string>('');
  const token = user?.token;
  const initialStatedata = { stateId: 0, stateName: "" };
  const [States, setStates] = useState([initialStatedata]);
  const [summaryData, setSummaryData] = useState<{DueInNext5Days: number, DueToday: number, PastDue: number}>({DueInNext5Days: 0, DueToday: 0, PastDue: 0});
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedStateReport, setSelectedStateReport] = useState<StateReportModel | null>(null);
  const fetchStateData = () => {
    apiClient("/States/getStates", {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
    })
      .then((responseJson) => {
        if (!responseJson) return;
        setStates(responseJson.data);
      });
  };

  const getPartnersList = () => {
    apiClient("/customer/getPartnersList", {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
    })
      .then((responseJson) => {
        if (!responseJson) return;
        if (responseJson.data && responseJson.data.length > 0) {
          setPartners(responseJson.data);
        } else {
          setPartners([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching partners:", error);
        setPartners([]);
      });
  };

  const getSummaryData = () => {
    apiClient("/StateReport/StateReportSummary", {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
    })
    .then((responseJson) => {
      if (!responseJson) return;
      if (responseJson.status && responseJson.data) {
        setSummaryData(responseJson.data);
      } else {
        setSummaryData({DueInNext5Days: 0, DueToday: 0, PastDue: 0});
      }
    })
    .catch((error) => {
      console.error("Error fetching summary data:", error);
      setSummaryData({DueInNext5Days: 0, DueToday: 0, PastDue: 0});
    });
  };

  useEffect(() => {
    getSummaryData();
    fetchStateData();
    getPartnersList();
  }, []);

  useEffect(() => {
    fetchData();
  }, [pageNumber, pageSize, filterModel, sortModel, selectedTab]);

  const fetchData = async () => {
    setloading(true);
    const res = await fetchDataMain(pageSize, pageNumber);
    if (res) {
      setItemList(res.data ?? []);
      setTotalCount(res.totalCount ?? 0);
    }
    setloading(false);
  };

  const fetchDataMain = async (_pageSize: number, _pageNumber: number) => {
    try {
      const requestBody = {
        pageSize: _pageSize || 50,
        currentPage: _pageNumber || 1,
        partnerId: selectedPartnerRef.current || '',
        stateId: selectedStateRef.current || '',
        startDate: startDateRef.current || '',
        endDate: endDateRef.current || '',
        field: sortModel?.[0]?.['field'] || '',
        sort: sortModel?.[0]?.['sort'] || '',
        fieldName: filterModel?.items?.[0]?.['columnField'] || '',
        value: filterModel?.items?.[0]?.['value'] || '',
        operator: filterModel?.items?.[0]?.['operatorValue'] || '',
        selectedTab: selectedTab || 0
      };

      const res = await apiClient(`/StateReport/ClientStateReports`, {
        method: "POST",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        }),
        body: JSON.stringify(requestBody),
      });
      if (!res || !res.status) return null;
      return res;
    } catch (error) {
      console.error("Error fetching data:", error);
      return null;
    }
  };

  const RunSearch = async () => {
    setloading(true);
    startDateRef.current = startDate ? startDate.format('MM/DD/YYYY') : '';
    endDateRef.current = endDate ? endDate.format('MM/DD/YYYY') : '';
    selectedPartnerRef.current = selectedPartner;
    selectedStateRef.current = Number(selectedHomeState);
    fetchData();
  };

  const ClearFilter = () => {
    setloading(true);
    setSelectedHomeState('');
    setStartDate(null);
    setEndDate(null);
    setSelectedPartner('');
    startDateRef.current = '';
    endDateRef.current = '';
    selectedPartnerRef.current = '0';
    selectedStateRef.current = 0;
    fetchData();
  };

  const handleExport = async () => {
    try {
      setloading(true);

      // Use current table data instead of fetching all data
      const exportData = itemList;

      if (!exportData || exportData.length === 0) {
        Swal.fire({
          icon: 'error',
          title: 'Export Failed',
          text: 'No data available to export',
          confirmButtonText: 'OK'
        });
        setloading(false);
        return;
      }

      // Filter columns to exclude "Done", "View", and hidden columns
      // Only include "Client" column if there are multiple partners
      const exportableColumns = columns.filter((column) => {
        // Exclude hidden columns
        if (column.hide) return false;
        // Exclude "Done" and "View" columns
        if (column.field === 'isDoneByClient' || column.field === 'view') return false;
        // Only include "Client" if there are multiple partners
        if (column.field === 'client' && partners.length <= 1) return false;
        return true;
      });

      // Get headers from filtered columns
      const headers = exportableColumns.map((column) => column.headerName || '');

      // Prepare data for export - match UI display logic
      const exportRows = exportData.map((item: StateReportModel) => {
        const row: any[] = [];
        
        exportableColumns.forEach((column) => {
          const field = column.field;
          let value: any = '';
          
          if (field === 'dueDate') {
            value = item.dueDate ? safeMoment(item.dueDate).format("MM/DD/YYYY") : '';
          } else if (field === 'stateName') {
            value = item.stateName || '';
          } else if (field === 'licensee') {
            value = item.licensee || '';
          } else if (field === 'client') {
            value = item.client || '';
          } else if (field === 'name') {
            value = item.name || '';
          } else if (field === 'period') {
            value = item.period || '';
          } else if (field === 'feeDue') {
            const feeDue = item.feeDue || '';
            const numericValue = typeof feeDue === 'string' 
              ? parseFloat(feeDue.replace(/[,$]/g, "").trim()) 
              : parseFloat(String(feeDue));
            value = isNaN(numericValue) ? feeDue : formatPrice(numericValue.toString());
          } else {
            // Fallback to direct property access
            value = (item as any)[field] || '';
          }
          
          row.push(value);
        });
        
        return row;
      });

      // Combine headers and data
      const exportArray = [headers, ...exportRows];

      // Generate filename with timestamp
      const timestamp = moment().format('YYYY-MM-DD_HH-mm-ss');
      const filename = `StateReports_${exportData.length}_${timestamp}.xlsx`;

      // Export to Excel
      await writeXlsxFile(exportArray, filename);

      // Show success popup
      Swal.fire({
        icon: 'success',
        title: 'Export Successful',
        text: `Successfully Exported.`,
        confirmButtonText: 'OK'
      });
    } catch (error) {
      console.error('Export error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Export Failed',
        text: 'An error occurred while exporting the data',
        confirmButtonText: 'OK'
      });
    } finally {
      setloading(false);
    }
  };

  const handleDoneChange = (stateReportId: number, status: boolean) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `You want to mark this report as ${status ? 'Completed' : 'Revised'}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
    }).then((result) => {
      if (result.isConfirmed) {
        setloading(true);
        apiClient(`/StateReport/UpdateClientStateReportStatus`, {
          method: "POST",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          }),
          body: JSON.stringify({
            stateReportId: stateReportId,
            status: status ? 'Completed' : 'Revised',
          }),
        }).then((res) => {
          if (!res) return;
          if (res.status) {
            Swal.fire({
              title: 'Success!',
              text: `The report has been marked as ${status ? 'Completed' : 'Revised'}.`,
              icon: 'success',
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: res.message,
              icon: 'error',
            });
          }
        }).catch((error) => {
          console.error(`Error marking report as ${status ? 'Completed' : 'Revised'}:`, error);
          Swal.fire({
            title: 'Error!',
            text: `Failed to mark report as ${status ? 'Completed' : 'Revised'}.`,
            icon: 'error',
          });
        }).finally(() => {
          fetchData();
          getSummaryData();
          setloading(false);
        });
      }
    });
  };

  const columns: GridColDef[] = [
    {
      field: 'customerPolicyId',
      hide: true,
    },
    {
      field: 'isDoneByClient',
      headerName: 'Done',
      width: 100,
      minWidth: 100,
      flex: 0.5,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Checkbox
          checked={params.row.isDoneByClient === 'true' || false}
          onChange={(e) => handleDoneChange(params.row.stateReportId, e.target.checked)}
          sx={{ padding: 0 }}
        />
      ),
    },
    {
      field: 'dueDate',
      hideSortIcons: true,
      type: 'string',
      headerName: 'Due Date',
      flex: 0.5,
      align: 'left',
      headerAlign: 'left',
      minWidth: 180,
      width: 180,
      renderCell: (params) => {
        if (!params.value) {
          return <span style={{ color: 'inherit' }}>{params.value}</span>;
        }

        let dueDate;
        try {
          dueDate = new Date(params.value);
          if (isNaN(dueDate.getTime())) {
            throw new Error('Invalid date');
          }
        } catch (error) {
          console.error('Invalid date value:', params.value, error);
          return <span style={{ color: 'inherit' }}>{params.value}</span>;
        }

        const currentDate = new Date();
        const isLate = dueDate < currentDate;

        return (
          <span style={{ color: isLate ? 'red' : 'inherit' }}>
            {safeMoment(dueDate).format("MM/DD/YYYY")}
          </span>
        );
      },
      renderHeader: (params) => {
          const sortItem = sortModel.find(
              (item: any) => item.field === 'dueDate'
            );
      
          const isSortedAsc = sortItem?.sort === 'asc';
          const isSortedDesc = sortItem?.sort === 'desc'
          return (
              <div>
                  <span className="table-card-title">{params.colDef.headerName}</span>
                  {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
              </div>
          )
      },
    },
    {
      field: 'stateName',
      hideSortIcons: true,
      headerName: 'State',
      flex: 1,
      minWidth: 100,
      width: 100,
      align: 'left',
      headerAlign: 'left',
      renderHeader: (params) => {
          const sortItem = sortModel.find(
              (item: any) => item.field === 'stateName'
            );
      
          const isSortedAsc = sortItem?.sort === 'asc';
          const isSortedDesc = sortItem?.sort === 'desc'
          return (
              <div>
                  <span className="table-card-title">{params.colDef.headerName}</span>
                  {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
              </div>
          )
      },
    },
    {
      field: 'licensee',
      headerName: 'Licensee',
      hideSortIcons: true,
      flex: 1,
      minWidth: 200,
      width: 200,
      align: 'left',
      headerAlign: 'left',
      renderHeader: (params) => {
          const sortItem = sortModel.find(
              (item: any) => item.field === 'licensee'
            );
      
          const isSortedAsc = sortItem?.sort === 'asc';
          const isSortedDesc = sortItem?.sort === 'desc'
          return (
              <div>
                  <span className="table-card-title">{params.colDef.headerName}</span>
                  {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
              </div>
          )
      },
    },
    ...(partners.length > 1 ? [{
      field: 'client',
      headerName: 'Client',
      hideSortIcons: true,
      flex: 1,
      minWidth: 300,
      width: 300,
      align: 'left',
      headerAlign: 'left',
      renderHeader: (params) => {
          const sortItem = sortModel.find(
              (item: any) => item.field === 'client'
            );
      
          const isSortedAsc = sortItem?.sort === 'asc';
          const isSortedDesc = sortItem?.sort === 'desc'
          return (
              <div>
                  <span className="table-card-title">{params.colDef.headerName}</span>
                  {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
              </div>
          )
      },
      
    } as GridColDef] : []),
    {
      field: 'name',
      headerName: 'Report Name',
      flex: 1,
      minWidth: 380,
      width: 380,
      hideSortIcons: true,
      align: 'left',
      headerAlign: 'left',
      renderHeader: (params) => {
          const sortItem = sortModel.find(
              (item: any) => item.field === 'name'
            );
      
          const isSortedAsc = sortItem?.sort === 'asc';
          const isSortedDesc = sortItem?.sort === 'desc'
          return (
              <div>
                  <span className="table-card-title">{params.colDef.headerName}</span>
                  {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
              </div>
          )
      },
    },
    {
      field: 'period',
      headerName: 'Period',
      flex: 1,
      minWidth: 100,
      width: 100,
      hideSortIcons: true,
      align: 'left',
      headerAlign: 'left',
      renderHeader: (params) => {
          const sortItem = sortModel.find(
              (item: any) => item.field === 'period'
            );
      
          const isSortedAsc = sortItem?.sort === 'asc';
          const isSortedDesc = sortItem?.sort === 'desc'
          return (
              <div>
                  <span className="table-card-title">{params.colDef.headerName}</span>
                  {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
              </div>
          )
      },
    },
    {
      field: 'feeDue',
      headerName: 'Amount Due',
      flex: 1,
      minWidth: 170,
      // width: 130,
      align: 'right',
      headerAlign: 'right',
      hideSortIcons: true,
      renderCell: (params) => {
        let feeDue = params.value || '';
        const numericValue = parseFloat(feeDue);
        return isNaN(numericValue) ? feeDue : `${formatPrice(numericValue.toString())}`;
      },
      renderHeader: (params) => {
          const sortItem = sortModel.find(
              (item: any) => item.field === 'feeDue'
            );
      
          const isSortedAsc = sortItem?.sort === 'asc';
          const isSortedDesc = sortItem?.sort === 'desc'
          return (
              <div>
                  <span className="table-card-title">{params.colDef.headerName}</span>
                  {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
              </div>
          )
      },
    },
    {
      field: 'view',
      headerName: 'View',
      flex: 0.5,
      minWidth: 80,
      width: 80,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          onClick={() => {
            setSelectedStateReport(params.row);
            setPreviewDialogOpen(true);
          }}
          sx={{ color: 'rgba(255,255,255,0.9)', '&:hover': { color: '#fff', backgroundColor: 'rgba(32,153,210,0.15) !important' } }}
        >
          <RemoveRedEyeOutlinedIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const handleTabChange = (tab: number) => {
    if (tab !== selectedTab) {
      setItemList([]);
      setTotalCount(0);
      setloading(true);
      setPageNumber(1);
      setSelectedTab(tab);
      ClearFilter();
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <div style={{ position: "fixed", top: "0", left: "0", zIndex: 9990, width: "100%" }}>
        {loading && <LinearProgress color="info" sx={{ backgroundColor: "#132D3D" }} />}
      </div>

      {/* Top bar: Tabs | Filters | Refresh | Export */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.5,
          borderBottom: "1px solid #e2e8f0",
          backgroundColor: "#fafbfc",
          minHeight: 56,
          mb: 1,
          marginLeft: { xs: "-24px", md: "0px" },
          marginRight: { xs: "-24px", md: "0px" },
        }}
      >
        <Tabs
          value={selectedTab}
          onChange={(_, newValue: number) => handleTabChange(newValue)}
          sx={{
            minHeight: 48,
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: "0.9375rem" },
            "& .Mui-selected": { color: "#2099d2" },
            "& .MuiTabs-indicator": {
              backgroundColor: "#2099d2",
              height: 3,
              borderRadius: "3px 3px 0 0",
            },
          }}
        >
          <Tab value={1} label="Outstanding" />
          <Tab value={2} label="Completed" />
        </Tabs>
        <Box sx={{ flex: 1, minWidth: 120 }} />
        <Button
          variant="outlined"
          size="medium"
          onClick={() => window.location.reload()}
          startIcon={<Refresh />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderColor: "#e2e8f0",
            color: "#475569",
            "&:hover": {
              borderColor: "#2099d2",
              backgroundColor: "#2099d214",
              color: "#2099d2",
            },
          }}
        >
          Refresh
        </Button>
        <Button
          variant="outlined"
          size="medium"
          onClick={handleExport}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderColor: "#e2e8f0",
            color: "#475569",
            "&:hover": {
              borderColor: "#2099d2",
              backgroundColor: "#2099d214",
              color: "#2099d2",
            },
          }}
        >
          Export
        </Button>
      </Box>

      <Box
        sx={{
          width: "100%",
          py: 2,
          px: 2,
          backgroundColor: "#ffffff",
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} className="filter-grid" display="flex">
              <Grid item xl={2} lg={4} sm={6} xs={12}>
                <FormControl fullWidth size="small">
                  <Autocomplete
                    id="home-state"
                    sx={{
                      marginTop: '-4px !important',
                      '& .MuiAutocomplete-input': {
                        padding: '0px 4px 0px 5px !important',
                      }
                    }}
                    options={States.filter((s) => s.stateId !== 0)}
                    getOptionLabel={(option) => option.stateName || ""}
                    onChange={(_event, value) => {
                      setSelectedHomeState(value ? String(value.stateId) : "");
                    }}
                    value={
                      selectedHomeState === ""
                        ? null
                        : States.find((item) => String(item.stateId) === String(selectedHomeState)) ?? null
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Home State"
                        sx={{
                          '.MuiFormLabel-root.Mui-focused': {
                            color: (typeof params.inputProps.value === 'string' && params.inputProps.value.length > 0) ? '#000' : '#cccccc !important'
                          },
                          '& .MuiInputBase-root': { marginTop: '5px !important' },
                          '& .MuiOutlinedInput-root': { padding: '4.5px 9px' },
                          '& .MuiFormLabel-root': { transform: 'translate(14px, 13px) scale(1)' },
                          '& .MuiInputLabel-shrink': { transform: 'translate(14px, -2px) scale(0.75)' },
                        }}
                        InputLabelProps={{
                          ...params.InputLabelProps,
                          shrink:
                            (selectedHomeState !== "" &&
                              selectedHomeState !== "0") ||
                            (typeof params.inputProps.value === "string" && params.inputProps.value.length > 0)
                              ? true
                              : false,
                        }}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xl={2} lg={4} sm={6} xs={12}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#c4c4c4" },
                        "& .MuiInputBase-root": { marginTop: "1px !important" },
                        "& .MuiInputBase-input": { padding: "4.5px 14px !important" },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xl={2} lg={4} sm={6} xs={12}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#c4c4c4" },
                        "& .MuiInputBase-root": { marginTop: "1px !important" },
                        "& .MuiInputBase-input": { padding: "4.5px 14px !important" },
                      }}
                    />
                  )}
                />
              </Grid>
              {partners.length > 1 && (
                <Grid item xl={2} lg={4} sm={6} xs={12}>
                  <FormControl size="small" fullWidth>
                    <Autocomplete
                      id="select-client"
                      sx={{
                        marginTop: "-4px !important",
                        "& .MuiAutocomplete-input": {
                          padding: "0px 4px 0px 5px !important",
                        },
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#c4c4c4" },
                      }}
                      options={partners}
                      getOptionLabel={(option) => option.customerName || ""}
                      onChange={(_event, value) => {
                        setSelectedPartner(value ? String(value.partnersId) : "");
                      }}
                      value={
                        selectedPartner === ""
                          ? null
                          : partners.find((item) => String(item.partnersId) === String(selectedPartner)) ?? null
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Client"
                          sx={{
                            ".MuiFormLabel-root.Mui-focused": {
                              color:
                                typeof params.inputProps.value === "string" && params.inputProps.value.length > 0
                                  ? "#000"
                                  : "#cccccc !important",
                            },
                            "& .MuiInputBase-root": { marginTop: "5px !important" },
                            "& .MuiOutlinedInput-root": { padding: "4.5px 9px" },
                            "& .MuiFormLabel-root": { transform: "translate(14px, 13px) scale(1)" },
                            "& .MuiInputLabel-shrink": { transform: "translate(14px, -2px) scale(0.75)" },
                            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#c4c4c4" },
                          }}
                          InputLabelProps={{
                            ...params.InputLabelProps,
                            shrink:
                              (selectedPartner !== "" &&
                                selectedPartner !== "0") ||
                              (typeof params.inputProps.value === "string" && params.inputProps.value.length > 0)
                                ? true
                                : false,
                          }}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} sm="auto">
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    size="medium"
                    onClick={() => RunSearch()}
                    startIcon={<Search />}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      backgroundColor: "#2099d2",
                      "&:hover": { backgroundColor: "#176990" },
                    }}
                  >
                    Search
                  </Button>
                  <Button
                    variant="outlined"
                    size="medium"
                    onClick={() => ClearFilter()}
                    startIcon={<Clear />}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      borderColor: "#e2e8f0",
                      color: "#475569",
                      "&:hover": { borderColor: "#2099d2", color: "#2099d2" },
                    }}
                  >
                    Clear
                  </Button>
                </Box>
              </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>

      {/* Welcome + summary card */}
      <Box
        sx={{
          borderBottom: "1px solid #e2e8f0",
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          mb: 2,
          px: 3,
          py: 2.5,
          boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
          border: "1px solid #e2e8f0",
        }}
      >
        <Typography sx={{ fontSize: "1.5rem", fontWeight: 600, color: "#334155", mb: 0.25 }}>
          Hello, {currentUser?.userFirstName} {currentUser?.userlastName}!
        </Typography>
        <Typography sx={{ fontSize: "0.875rem", color: "#64748b", mb: 1 }}>
          Today: {formatUtcToLocal(new Date().toISOString(), 'dddd, MMMM D, YYYY')}
        </Typography>
        <Typography sx={{ fontSize: "0.9375rem", fontWeight: 400, color: "#64748b", mb: selectedTab === 1 ? 2 : 0 }}>
          {selectedTab === 1 ? (
            <>
              Below, please find a list of State Reports that our team has processed on your behalf which now requires action from your team. You may review the details and instructions for each report by selecting the <strong>View</strong> button to the right. Check <strong>Done</strong> to mark an item as Completed and move it to your Completed Queue.
            </>
          ) : (
            <>
              Below, please find a historical list of your Completed State Reports. You may review the details and attached documents on any report by selecting the <strong>View</strong> button to the right.
            </>
          )}
        </Typography>
        {selectedTab === 1 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1.5 }}>
            <Box sx={{ px: 2, py: 1, borderRadius: "8px", backgroundColor: "#fef2f2", color: "#b91c1c", fontWeight: 600, fontSize: "0.875rem" }}>
              Past Due: {summaryData.PastDue ?? 0}
            </Box>
            <Box sx={{ px: 2, py: 1, borderRadius: "8px", backgroundColor: "#f8fafc", color: "#334155", fontWeight: 600, fontSize: "0.875rem" }}>
              Due Today: {summaryData.DueToday ?? 0}
            </Box>
            <Box sx={{ px: 2, py: 1, borderRadius: "8px", backgroundColor: "#f0f9ff", color: "#2099d2", fontWeight: 600, fontSize: "0.875rem" }}>
              Due in Next 5 Days: {summaryData.DueInNext5Days ?? 0}
            </Box>
          </Box>
        )}
      </Box>

      {/* Grid wrapper */}
      <Box
        sx={{
          height: { xs: "calc(100vh - 320px)", sm: "calc(100vh - 320px)" },
          display: "flex",
          flexDirection: "column",
          width: "100%",
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 4px 24px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(15, 23, 42, 0.06)",
          border: "1px solid #e2e8f0",
          backgroundColor: "#ffffff",
          marginBottom: 4,
          position: "relative",
        }}
      >
        {loading && (<Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{
                backgroundColor: "rgba(250, 251, 252, 0.85)",
                zIndex: 10,
                border: "1px solid #e2e8f0",
                borderRadius: 1,
            }}
        >
            <Box sx={{ width: "100%", maxWidth: 320 }}>
                <LinearProgress color="info" sx={{ backgroundColor: "#e2e8f0", "& .MuiLinearProgress-bar": { backgroundColor: "#132D3D" } }} />
                <Box sx={{ mt: 1.5, textAlign: "center" }}>
                    <span style={{ fontSize: "14px", fontWeight: 600, color: "#475569" }}>Loading...</span>
                </Box>
            </Box>
        </Box>)}
        <Box sx={{ flex: "1 1 0%", overflow: "auto", minHeight: 0 }}>
        <DataGrid
          getRowId={(r) => r.rowNum}
          rows={itemList}
          columns={columns}
          rowCount={totalCount}
          pageSize={pageSize}
          loading={loading}
          pagination
          paginationMode="server"
          onPageChange={(newPage: number) => setPageNumber(newPage + 1)}
          rowsPerPageOptions={[10, 25, 50]}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          filterMode="server"
          onFilterModelChange={(newFilterModel: any) => setFilterModel(newFilterModel)}
          sortingMode="server"
          sortingOrder={['asc', 'desc']}
          onSortModelChange={(newSortModel: any) => setSortModel(newSortModel)}
          hideFooterSelectedRowCount
          sx={{
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            border: 'none',
            fontSize: '14px',
            color: '#475569',
            backgroundColor: '#ffffff',
            "& .MuiDataGridPro-columnHeaderTitle": {
                whiteSpace: 'normal',
                lineHeight: 1.35,
                fontWeight: 600,
                fontSize: '12px',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                color: '#64748b',
            },
            "& .MuiDataGrid-columnHeaders": {
                backgroundColor: '#f8fafc',
                borderBottom: '2px solid #e2e8f0',
                borderTop: '2px solid #e2e8f0',
                minHeight: '52px !important',
                maxHeight: '52px !important',
            },
            "& .MuiDataGrid-columnHeader": {
                "&:focus, &:focus-within": { outline: 'none' },
            },
            "& .MuiDataGrid-row": {
                transition: 'background-color 0.15s ease',
                "&:nth-of-type(even)": {
                    backgroundColor: '#fafbfc',
                },
                "&:hover": {
                    backgroundColor: '#f1f5f9 !important',
                },
                "&.Mui-selected": {
                    backgroundColor: '#ebf5fa !important',
                    "&:hover": {
                        backgroundColor: '#dbeef7 !important',
                    },
                },
            },
            "& .MuiDataGrid-cell": {
                borderBottom: '1px solid #f1f5f9',
                fontSize: '14px',
                color: '#334155',
                "&:focus": { outline: 'none' },
            },
            "& .MuiDataGrid-virtualScroller": {
                border: 'none',
            },
            "& .MuiDataGrid-main": {
                border: 'none',
            },
            "& .MuiDataGrid-footerContainer": {
                borderTop: '1px solid #e2e8f0',
                backgroundColor: '#f8fafc',
                minHeight: 56,
            },
            "& .MuiTablePagination-root": {
                color: '#64748b',
                fontSize: '13px',
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                marginBottom: 0,
            },
            "& .MuiTablePagination-select": {
                fontSize: '13px',
                borderRadius: '6px',
                border: '1px solid #e2e8f0',
            },
            "& .MuiIconButton-root": {
                color: '#64748b',
                "&:hover": {
                    backgroundColor: '#f1f5f9',
                    color: '#475569',
                },
            },
            "& .MuiCheckbox-root": {
                color: '#94a3b8',
                "&.Mui-checked": {
                    color: '#2099d2',
                },
            },
        }}
        />
        </Box>
      </Box>

      {/* State Report Preview Dialog */}
      <StateReportPreviewDialog
        open={previewDialogOpen}
        onClose={() => {
          setPreviewDialogOpen(false);
          setSelectedStateReport(null);
        }}
        stateReport={selectedStateReport}
        selectedTab={selectedTab}
        states={States}
        onMarkAsDone={() => {
          fetchData();
          getSummaryData();
        }}
      />
    </Box>
  );
};
